import React, { useEffect, useRef, useState } from 'react'
import { useApiFormProviderOrganization } from '../../../../../../api-new/state/auth'
import { ENDS_WITH, providerOrganizationRuleClause } from '../provider-organization-rule'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

const OTHER = '__other__'

export const ENDS_WITH_ERROR = "Start with @ or . so fuzion.com can't also match evilfuzion.com."

// The hint under the claim, from the claim's name (and how it is matched)
// alone: which identity provider sent it doesn't enter into it.
export const ruleHint = (claim, match) => {
  if (claim === 'groups') return 'Group claims must be turned on in the identity provider; Microsoft sends group IDs, not names.'
  if (claim === 'email') return match === ENDS_WITH ? 'Matches the end of the address, such as @fuzion.com.' : 'Matches the whole address, ignoring capitalisation.'
  return match === ENDS_WITH ? 'Matches the end of each value, ignoring capitalisation.' : 'Matches values exactly, ignoring capitalisation.'
}

// "ends with" values must start with @ or ., so a domain can't match the
// tail of a longer one.
export const endsWithValid = value => value.startsWith('@') || value.startsWith('.')

// Whether an "ends with" value can match anyone this connection signs in:
// "@fuzion.com" needs the domain fuzion.com, ".fuzion.com" fuzion.com or
// any of its subdomains. domainNames are the connection's bound domains.
export const endsWithCovered = (value, domainNames) => {
  const v = value.toLowerCase()
  const names = domainNames.map(d => String(d).toLowerCase())
  if (v.startsWith('@')) return names.includes(v.slice(1))
  const base = v.slice(1)
  return names.some(d => d === base || d.endsWith(`.${base}`))
}

// The rule as a sentence: the matchData the editor would save.
export const ruleSentence = (organizationName, matchData) =>
  matchData
    ? `People join ${organizationName} when their ${providerOrganizationRuleClause(matchData)} (not case-sensitive).`
    : `People join ${organizationName} on every sign-in.`

// A list of strings as removable pills: type, Enter adds, × removes,
// Backspace in an empty box removes the last. Plain controlled inputs: this
// renders inside the page's FormNew, where FormNewTags would join (and
// submit) the page's form.
const TagsInput = ({ id, values, draft, onDraft, onChange, invalid, describedBy }) => {
  const input = useRef(null)
  // A click anywhere in the box (not on a pill's ×) types into the input.
  const handleBoxMouseDown = e => {
    if (e.target === e.currentTarget) { e.preventDefault(); input.current.focus() }
  }
  const add = () => {
    const v = draft.trim()
    if (v && !values.includes(v)) onChange([...values, v])
    onDraft('')
  }
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add() }
    else if (e.key === 'Backspace' && draft === '' && values.length) onChange(values.slice(0, -1))
  }
  return (
    <div className="sw-tags form-control" onMouseDown={handleBoxMouseDown} data-testid="sw-tags">
      {values.map(v =>
        <span key={v} className={`sw-tag${invalid && invalid(v) ? ' sw-tag-bad' : ''}`}>
          <span className="sw-mono">{v}</span>
          <button type="button" className="sw-tag-x" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter(x => x !== v))}>×</button>
        </span>)}
      <input id={id} ref={input} className="sw-tags-input" value={draft} onChange={e => onDraft(e.target.value)} onKeyDown={handleKeyDown}
        placeholder={values.length ? '' : 'Type a value and press Enter'} aria-describedby={describedBy} autoComplete="off" />
    </div>
  )
}

const initial = (link, claims) => {
  const m = link.matchData
  const claim = (m && m.claim) || ''
  return {
    who: m && m.claim ? 'match' : 'every',
    choice: !claim ? '' : claims.includes(claim) ? claim : OTHER,
    other: claim && !claims.includes(claim) ? claim : '',
    match: m && m.match === ENDS_WITH ? ENDS_WITH : 'is',
    values: (m && m.values) || []
  }
}

// Inline editor for one link's claim rule (replaces the modal inside the
// connection workspace). Everything is type="button" and Enter in any of its
// inputs is swallowed, so nothing here submits the page's form. Save PUTs
// /provider-organization/:providerID/:organizationID like the modal:
// {matchData: null} for every sign-in, else {claim, values} plus
// match: "ends_with" when chosen ("is" is the default and left out).
// domainNames: the connection's domains, or null while unknown (no
// warnings then).
export const OrgRuleEditor = ({ link, providerID, claims = [], domainNames = null, onSaved, onCancel }) => {
  const api = useApiFormProviderOrganization()
  const [form, setForm] = useState(() => initial(link, claims))
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const first = useRef(null)
  const idp = `sw-rule-${link.organizationID}`

  useEffect(() => { if (first.current) first.current.focus() }, [])

  const set = patch => setForm(f => ({ ...f, ...patch }))
  const claim = (form.choice === OTHER ? form.other : form.choice).trim()
  // A value typed but not yet committed with Enter still counts.
  const values = draft.trim() && !form.values.includes(draft.trim()) ? [...form.values, draft.trim()] : form.values
  const badValue = form.match === ENDS_WITH && values.some(v => !endsWithValid(v))
  const matchData = form.who === 'every' ? null : { claim, values, ...(form.match === ENDS_WITH ? { match: ENDS_WITH } : {}) }
  // Warned, not blocked: the domains may be about to change.
  const uncovered = form.match === ENDS_WITH && domainNames ? values.filter(v => endsWithValid(v) && !endsWithCovered(v, domainNames)) : []
  const complete = form.who === 'every' || (claim && values.length && !badValue)

  const handleKeyDown = e => {
    if (e.key === 'Escape') { e.stopPropagation(); onCancel() }
    else if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault()
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.put(`${providerID}/${link.organizationID}`, { matchData })
      onSaved()
    } catch (exp) {
      setError((exp && exp.data && exp.data.message) || 'Could not save the rule.', exp)
      setSaving(false)
    }
  }

  const sortedClaims = [...claims].sort((a, b) => a.localeCompare(b))

  return (
    <div className="sw-inline-panel" role="region" aria-label={`Rule for ${link.organizationName}`} onKeyDown={handleKeyDown}>
      <fieldset className="d-grid gap-2 m-0">
        <legend className="sw-label mb-1">Who joins {link.organizationName}</legend>
        <label className={`sw-choice${form.who === 'every' ? ' sw-checked' : ''}`}>
          <input type="radio" name={`${idp}-who`} value="every" checked={form.who === 'every'} onChange={() => set({ who: 'every' })}
            ref={form.who === 'every' ? first : null} />
          <span>Every sign-in</span>
        </label>
        <label className={`sw-choice${form.who === 'match' ? ' sw-checked' : ''}`}>
          <input type="radio" name={`${idp}-who`} value="match" checked={form.who === 'match'} onChange={() => set({ who: 'match' })}
            ref={form.who === 'match' ? first : null} />
          <span>Only people whose claim matches</span>
        </label>
      </fieldset>

      {form.who === 'match' &&
        <div className="sw-rule-fields">
          <div className="sw-field">
            <label className="sw-label" htmlFor={`${idp}-claim`}>Claim</label>
            <select id={`${idp}-claim`} className="form-select sw-mono" value={form.choice} onChange={e => set({ choice: e.target.value })} aria-describedby={`${idp}-hint`}>
              <option value="" disabled>Choose a claim</option>
              {sortedClaims.map(c => <option key={c} value={c}>{c}</option>)}
              <option value={OTHER}>Other…</option>
            </select>
            {form.choice === OTHER &&
              <input className="form-control sw-mono" aria-label="Claim name" value={form.other} onChange={e => set({ other: e.target.value })}
                placeholder="the claim's exact name" autoComplete="off" />}
            <span className="sw-hint" id={`${idp}-hint`}>{ruleHint(claim, form.match)}</span>
          </div>
          <div className="sw-field">
            <label className="sw-label" htmlFor={`${idp}-match`}>Match</label>
            <select id={`${idp}-match`} className="form-select" value={form.match} onChange={e => set({ match: e.target.value })}>
              <option value="is">is</option>
              <option value={ENDS_WITH}>ends with</option>
            </select>
          </div>
          <div className="sw-field">
            <label className="sw-label" htmlFor={`${idp}-values`}>Values</label>
            <TagsInput id={`${idp}-values`} values={form.values} draft={draft} onDraft={setDraft} onChange={v => set({ values: v })}
              invalid={form.match === ENDS_WITH ? v => !endsWithValid(v) : null} describedBy={badValue ? `${idp}-bad` : undefined} />
            {badValue && <span className="text-danger small" id={`${idp}-bad`} role="alert">{ENDS_WITH_ERROR}</span>}
            {uncovered.map(v => <span key={v} className="sw-warn small" data-testid="sw-domain-warning">{v} isn't one of this connection's domains, so nobody signing in through it is likely to match.</span>)}
          </div>
        </div>}

      <p className="sw-note m-0" aria-live="polite">
        {complete ? ruleSentence(link.organizationName, matchData) : 'Choose a claim and add at least one value.'}
      </p>
      <p className="sw-hint m-0">To combine conditions, create a group in your identity provider and match on it.</p>
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}
      <div className="sw-inline-actions">
        <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn sw-btn-primary" disabled={!complete || saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save rule'}</button>
      </div>
    </div>
  )
}
